import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BRAND, LOGOS } from "@/lib/eraContent";
import { LANGUAGES } from "@/lib/i18n";
import { useLang } from "@/lib/LanguageContext";
import { cn } from "@/lib/utils";

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  const top = el.getBoundingClientRect().top + window.scrollY - 88;
  window.scrollTo({ top, behavior: "smooth" });
};

const SECTION_IDS = ["ecosystem", "about", "industries", "why-era", "contact"];

export const Navbar = () => {
  const { t, lang, setLang } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [activeId, setActiveId] = useState("ecosystem");
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const sections = SECTION_IDS.map((id) => document.getElementById(id)).filter(Boolean);
    if (!sections.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { rootMargin: "-40% 0px -55% 0px", threshold: 0 },
    );
    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, []);

  const handleNav = (id) => {
    setMobileOpen(false);
    scrollToId(id);
  };

  const NAV_ITEMS = [
    { id: "ecosystem", label: t.nav.ecosystem },
    { id: "about", label: t.nav.about },
    { id: "industries", label: t.nav.industries },
    { id: "why-era", label: t.nav.whyEra },
    { id: "contact", label: t.nav.contact },
  ];

  return (
    <header
      data-testid="site-navbar"
      className={cn(
        "fixed top-0 inset-x-0 z-50 transition-all duration-300",
        // Always solid white navbar — architectural bg image lives below in BU section only
        scrolled
          ? "bg-white border-b border-navy-100/70 shadow-[0_2px_20px_-8px_rgba(13,27,62,0.08)]"
          : "bg-white border-b border-transparent",
      )}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Bare logo — no white pill, no rounded rectangle, no container */}
          <button
            data-testid="navbar-brand"
            onClick={() => handleNav("ecosystem")}
            className="flex items-center gap-3"
            aria-label={BRAND.fullName}
          >
            <img
              src={LOGOS.group}
              alt={BRAND.fullName}
              className="h-10 sm:h-12 w-auto object-contain"
            />
            <span className="sr-only">{BRAND.fullName}</span>
          </button>

          <nav className="hidden lg:flex items-center gap-1">
            {NAV_ITEMS.map((link) => {
              const isActive = activeId === link.id;
              return (
                <button
                  key={link.id}
                  data-testid={`nav-link-${link.id}`}
                  onClick={() => handleNav(link.id)}
                  className={cn(
                    "relative px-4 py-2 text-sm font-medium transition-colors rounded-full",
                    isActive
                      ? "text-navy-900"
                      : "text-navy-700/70 hover:text-navy-900",
                  )}
                >
                  {link.label}
                  {isActive && (
                    <motion.span
                      layoutId="nav-active-underline"
                      className="absolute inset-x-3 -bottom-0.5 h-[3px] rounded-full bg-gold"
                      transition={{ type: "spring", stiffness: 380, damping: 30 }}
                    />
                  )}
                </button>
              );
            })}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <LanguageSwitcher
              lang={lang}
              setLang={setLang}
              label={t.nav.langLabel}
              variant="desktop"
            />
            <Button
              data-testid="navbar-contact-cta"
              onClick={() => handleNav("contact")}
              className="rounded-full px-5 h-10 font-semibold bg-navy-900 hover:bg-navy-800 text-white"
            >
              {t.nav.cta}
            </Button>
          </div>

          <div className="flex lg:hidden items-center gap-2">
            <LanguageSwitcher
              lang={lang}
              setLang={setLang}
              label={t.nav.langLabel}
              variant="mobile"
              compact
            />
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setMobileOpen((v) => !v)}
              className="inline-flex items-center justify-center h-11 w-11 rounded-full border border-navy-100 bg-white text-navy-900 transition-colors"
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            data-testid="mobile-menu-panel"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.2 }}
            className="lg:hidden bg-white border-t border-navy-100"
          >
            <div className="px-4 sm:px-6 py-6 flex flex-col gap-2">
              {NAV_ITEMS.map((link) => (
                <button
                  key={link.id}
                  data-testid={`mobile-nav-link-${link.id}`}
                  onClick={() => handleNav(link.id)}
                  className={cn(
                    "text-left px-4 py-3 rounded-2xl font-medium",
                    activeId === link.id
                      ? "bg-navy-900 text-white"
                      : "text-navy-700 hover:bg-navy-50",
                  )}
                >
                  {link.label}
                </button>
              ))}
              <Button
                data-testid="mobile-contact-cta"
                onClick={() => handleNav("contact")}
                className="mt-2 rounded-full bg-navy-900 hover:bg-navy-800 text-white font-semibold h-11"
              >
                {t.nav.cta}
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

const LanguageSwitcher = ({ lang, setLang, label, compact = false, variant = "desktop" }) => {
  return (
    <div
      role="group"
      aria-label={label}
      data-testid={`language-switcher-${variant}`}
      className={cn(
        "inline-flex items-center rounded-full p-0.5 text-xs font-semibold tracking-wider bg-navy-50 border border-navy-100 text-navy-700",
        compact && "text-[11px]",
      )}
    >
      {LANGUAGES.map((l) => {
        const active = l.code === lang;
        return (
          <button
            key={l.code}
            type="button"
            onClick={() => setLang(l.code)}
            data-testid={`lang-btn-${variant}-${l.code}`}
            aria-pressed={active}
            className={cn(
              "px-2.5 sm:px-3 py-1.5 rounded-full transition-all",
              active
                ? "bg-navy-900 text-white shadow-sm"
                : "opacity-70 hover:opacity-100",
            )}
          >
            {l.label}
          </button>
        );
      })}
    </div>
  );
};
